import React, { Component } from "react";
import { MessageSourceProps, withMessages } from 'react-message-source';
import styled from 'styled-components';
import ToolbarButton from 'components/layout/ToolbarButton';
import { ConsultationDto, CostEstimationCompleteDto, PatientDto, TreatmentDto } from 'ts-types/api.types';
import UpsertConsultation from 'components/consultations/UpsertConsultation';
import { deleteConsultationById } from 'services/consultationServices';
import { EntriesConsumerRenderProps, withEntriesContext } from 'components/segments/entires/EntriesContext';
import { isCostEstimationComplete } from 'services/costEstimationServices';
import ConfirmActionModal from 'components/ConfirmActionModal';
import { closeTreatment } from 'services/treatmentServices';
import ConfirmActionPopup from 'components/ConfirmActionPopup';
import { BillStatus } from 'ts-types/api.enums';
import axios, { CancelTokenSource } from 'axios';
import { noop } from 'utils/functionUtils';

const ConsultationsToolbarContainer = styled.div`
  width: 70%;
  display: inline;
  flex: 1;
  text-align: right;
`;

interface Props extends MessageSourceProps, EntriesConsumerRenderProps {
  currentPatient: PatientDto,
  currentTreatment: TreatmentDto,
  editTreatment: (treatment: TreatmentDto) => void,
  currentConsultation: ConsultationDto
  addConsultation: (consultation: ConsultationDto) => void
  editConsultation: (consultation: ConsultationDto) => void
  deleteConsultation: () => void,
  setConsultationId: () => void,
  onOpenUpsertModal: () => void,
  onCloseUpsertModal: () => void,
  setCacheSearchId: (searchId?: string) => void
  consultationId?: number
}

interface State {
  consultationFormOpen: boolean,
  modalOpen: boolean,
  isCostEstimationComplete: boolean,
  cancelTokenSource: CancelTokenSource
}

class ConsultationsToolbar extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      consultationFormOpen: false,
      modalOpen: false,
      isCostEstimationComplete: false,
      cancelTokenSource
    };
  }

  onConfirmActionModal = () => {

    const { currentTreatment: { id }, editTreatment } = this.props;
    const { cancelTokenSource } = this.state;

    closeTreatment(id, cancelTokenSource)
        .then(response => {
          editTreatment(response);
          this.toggleConfirmActionModal();
        }).catch(noop);
  };

  onCancelActionModal = () => {

    const { onOpenUpsertModal } = this.props;

    this.toggleConfirmActionModal();
    onOpenUpsertModal();
  };

  toggleConfirmActionModal = () => {
    this.setState(({ modalOpen }: State) => ({
      modalOpen: !modalOpen
    }))
  };

  addConsultation = () => {

    const { currentTreatment: { id, costEstimationId } } = this.props;
    const { cancelTokenSource } = this.state;

    if (costEstimationId) {
      const costEstimationCompleteDto: CostEstimationCompleteDto = {
        treatmentId: id,
        costEstimationId: costEstimationId
      };

      isCostEstimationComplete(costEstimationCompleteDto, cancelTokenSource)
          .then(response => {
            if (response) {
              this.toggleConfirmActionModal();
            } else {
              this.props.onOpenUpsertModal();
            }

            this.setState({
              isCostEstimationComplete: response
            })
          }).catch(noop)
    } else {
      this.props.onOpenUpsertModal();
    }
  };

  removeConsultation = () => {
    const { currentConsultation: { id: consultationId }, deleteConsultation } = this.props;
    const { cancelTokenSource } = this.state;

    deleteConsultationById(consultationId, cancelTokenSource)
        .then(deleteConsultation)
        .catch(noop);
  };

  render(): React.ReactNode {

    const {
      currentPatient,
      currentTreatment,
      currentConsultation,
      getMessage,
      addConsultation,
      editConsultation,
      setCacheSearchId,
      consultationId,
      setConsultationId,
      onCloseUpsertModal
    } = this.props;
    const { consultationFormOpen, modalOpen, isCostEstimationComplete } = this.state;

    const isClosedOrBilled = currentTreatment && (currentTreatment.status || BillStatus.BILL == currentTreatment.billStatus);
    const isCurrentTreatmentDeleted = currentTreatment && currentTreatment.deleted === 1;

    return (
        <ConsultationsToolbarContainer>
          <ToolbarButton iconName='plus'
                         iconSize='large'
                         disabled={!currentPatient || !currentTreatment || isClosedOrBilled || isCurrentTreatmentDeleted}
                         onClick={this.addConsultation}
                         dataTooltip={getMessage('consultation.add')}
          />
          <ToolbarButton iconName='edit outline'
                         iconSize='large'
                         disabled={!currentPatient
                             || !currentTreatment
                             || !currentConsultation
                             || currentConsultation.billStatus == BillStatus.BILL
                             || currentTreatment.status
                             || isCurrentTreatmentDeleted}
                         onClick={setConsultationId}
                         dataTooltip={getMessage('consultation.edit')}
          />
          <ConfirmActionPopup
              renderTarget={this.renderDeleteTrigger()}
              onConfirmAction={this.removeConsultation}
              message={getMessage("consultation.confirmDelete")}
              disabled={!currentPatient
                  || !currentTreatment
                  || !currentConsultation
                  || currentConsultation.billStatus == BillStatus.BILL
                  || currentTreatment.status
                  || isCurrentTreatmentDeleted}
          />
          {consultationFormOpen && (
              <UpsertConsultation
                  consultationFormOpen={consultationFormOpen}
                  isCostEstimationComplete={isCostEstimationComplete}
                  onCloseUpsertModal={onCloseUpsertModal}
                  currentTreatment={currentTreatment}
                  currentPatient={currentPatient}
                  consultationId={consultationId}
                  addConsultation={addConsultation}
                  editConsultation={editConsultation}
                  setCacheSearchId={setCacheSearchId}
              />)
          }
          <ConfirmActionModal
              messageHeader={getMessage("treatment.close")}
              message={getMessage("costEstimation.complete")}
              modalOpen={modalOpen}
              onCancelAction={this.onCancelActionModal}
              onConfirmAction={this.onConfirmActionModal}
          />
        </ConsultationsToolbarContainer>
    );
  }

  renderDeleteTrigger = (): React.ReactNode => {

    const { currentPatient, currentTreatment, currentConsultation, getMessage } = this.props;

    const isCurrentTreatmentDeleted = currentTreatment && currentTreatment.deleted === 1;

    return (
        <ToolbarButton iconName='trash alternate outline'
                       iconSize='large'
                       disabled={!currentPatient
                           || !currentTreatment
                           || !currentConsultation
                           || currentConsultation.billStatus == BillStatus.BILL
                           || currentTreatment.status
                           || isCurrentTreatmentDeleted}
                       dataTooltip={getMessage('consultation.delete')}
        />
    )
  }
}

export default withMessages(withEntriesContext(ConsultationsToolbar));
